import React from "react";
import { motion } from "framer-motion";
import { useSiteCopy } from "@/lib/useSiteCopy";
import { Mail, MapPin } from "lucide-react";
import ContactForm from "./ContactForm";

/**
 * Classic's contact section: the editorial left column, plus the shared ContactForm skinned
 * with the classes the form used to carry inline. The form itself (fields, validation,
 * request, states) now lives in ContactForm.jsx and is shared with the five other themes.
 */
const CLASSIC_FORM_CLASSES = {
  grid: "grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7",
  fieldWide: "md:col-span-2",
  label: "text-xs uppercase tracking-[0.2em] text-eh-muted",
  input: "eh-input",
  textarea: "eh-input resize-none",
  hint: "mt-2 text-xs text-eh-muted",
  actions: "mt-10 flex items-center justify-between gap-4 flex-wrap",
  submit: "btn-primary disabled:opacity-60",
  success: "text-eh-sage-deep font-medium",
  error: "text-red-600 font-medium",
};

const Contact = () => {
  // siteCopy = dictionary + Site Content overrides + the tenant record (lib/useSiteCopy.js).
  const siteCopy = useSiteCopy();
  const contact = siteCopy.contact;

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-36 bg-eh-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-12 lg:col-span-5"
          >
            <span className="eyebrow">{contact.eyebrow}</span>
            <h2 className="font-display text-5xl md:text-6xl text-eh-ink mt-4 leading-[1.05] whitespace-pre-line">
              {contact.title}
            </h2>
            <p className="mt-6 text-lg md:text-xl text-eh-muted leading-relaxed max-w-md">{contact.lead}</p>

            <div className="mt-12 space-y-5">
              <a
                href={`mailto:${contact.info.email}`}
                className="flex items-center gap-4 group"
                data-testid="contact-email-link"
              >
                <span className="w-11 h-11 rounded-full bg-eh-sage/30 text-eh-sage-deep flex items-center justify-center group-hover:bg-eh-sage/45 transition">
                  <Mail size={16} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-eh-muted">{contact.email}</div>
                  <div className="font-display text-xl text-eh-ink break-all">{contact.info.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full bg-eh-sage/30 text-eh-sage-deep flex items-center justify-center">
                  <MapPin size={16} />
                </span>
                <div>
                  {/* Was the hardcoded French "Lieu" — the labels of this block are copy
                      like any other and belong to the locale layer. */}
                  <div className="text-xs uppercase tracking-[0.2em] text-eh-muted">{contact.placeLabel}</div>
                  <div className="font-display text-xl text-eh-ink">{contact.info.place}</div>
                </div>
              </div>
            </div>
          </motion.div>

          <ContactForm
            as={motion.form}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 lg:col-span-7 bg-eh-cream rounded-[2rem] border border-eh-border-soft p-8 md:p-12 shadow-[0_18px_50px_-25px_rgba(44,53,49,0.18)]"
            classNames={CLASSIC_FORM_CLASSES}
          />
        </div>
      </div>
    </section>
  );
};

export default Contact;
